import { Injectable, signal, OnDestroy } from '@angular/core';
import { DebugMenuComponent } from '@core/components/organisms/debug-menu/debug-menu.component';
import {
  PalantirConfirmDialogService,
  PalantirDialogService,
  PalantirToastService,
} from '@flux/design-system';
import { fluxLogger } from '../logger/logger.instance';

export const debugLogger = fluxLogger.extend('debug');

@Injectable({
  providedIn: 'root',
})
export class DebugService implements OnDestroy {
  private static INITIALIZED = false;

  private menuIsOpen = false;

  public isModifyingState = signal<boolean>(false);

  private DEBUG_METADATA_KEY = 'FLUX_DEBUG_METADATA';
  private SPECIAL_LOCAL_STORAGE_KEY = 'ENABLE_FLUX_DEBUG';
  private SPECIAL_SESSION_STORAGE_KEY = 'FLUX_DEBUG_MENU';

  private keydownHandler = (event: KeyboardEvent) => {
    if (this.menuIsOpen) {
      return;
    }

    const userAgent = navigator.userAgent.toLowerCase();
    const isMac = userAgent.includes('mac');

    const isD = event.key.toLowerCase() === 'd';

    // Mac: Command + Shift + D
    const isMacShortcut = isMac && event.metaKey && event.shiftKey && isD;

    // Windows/Linux: Ctrl + Shift + Alt + D
    const isWinLinuxShortcut = !isMac && event.ctrlKey && event.shiftKey && event.altKey && isD;

    if (isMacShortcut || isWinLinuxShortcut) {
      this.menuIsOpen = true;
      void this.openDebugMenu();
      event.preventDefault();
    }
  };

  public constructor(
    private readonly confirmDialogService: PalantirConfirmDialogService,
    private readonly palantirDialogService: PalantirDialogService,
    private readonly toastService: PalantirToastService,
  ) {
    if (DebugService.INITIALIZED) {
      debugLogger.warn('DebugService is already initialized.');
      return;
    }

    DebugService.INITIALIZED = true;

    this.listenToDebugKeyCombination();
    this.updateDebugModifyingState();

    setTimeout(() => {
      this.updateDebugModifyingState();
      if (this.isModifyingState()) {
        debugLogger.warn('Debug metadata is not empty.', this.getDebugMetadata());
        this.toastService.showWarning(
          'Atenção! O menu do desenvolvedor está ativamente modificando o estado da aplicação.',
          5000,
        );
      }
    }, 5000);
  }

  public isDebugEnabled(): boolean {
    return !!localStorage.getItem(this.SPECIAL_LOCAL_STORAGE_KEY);
  }

  public updateDebugModifyingState(): void {
    if (!this.isDebugEnabled()) {
      this.isModifyingState.set(false);
      return;
    }

    this.isModifyingState.set(Object.keys(this.getDebugMetadata()).length > 0);
  }

  public listenToDebugKeyCombination(): void {
    window.addEventListener('keydown', this.keydownHandler);
  }

  public async openDebugMenu() {
    const hasSpecialLocalStorageKey = localStorage.getItem(this.SPECIAL_LOCAL_STORAGE_KEY);
    const hasBeenOpened = sessionStorage.getItem(this.SPECIAL_SESSION_STORAGE_KEY);

    if (!hasSpecialLocalStorageKey && !hasBeenOpened) {
      debugLogger.warn('Debug menu is disabled.');
      return;
    }

    if (hasBeenOpened) {
      const debugRef = this.palantirDialogService.openDialog(DebugMenuComponent, {
        size: 'large',
      });

      debugRef.afterClosed().then(() => {
        this.menuIsOpen = false;
      });
      return;
    }

    const ref = this.confirmDialogService.openDialog({
      title: 'Menu do Desenvolvedor',
      text: 'Atenção! Este menu é destinado apenas para desenvolvedores. Qualquer ação realizada aqui pode impactar sua conta de formas imprevisíveis e irreversíveis. Tem certeza que deseja continuar?',
      successText: 'Sim, eu tenho certeza',
      cancelText: 'Cancelar',
    });

    const result = (await ref.afterClosed()) as { role: 'success' | 'cancel' } | undefined;

    if (result?.role !== 'success') {
      this.menuIsOpen = false;
      return;
    }

    sessionStorage.setItem(
      this.SPECIAL_SESSION_STORAGE_KEY,
      JSON.stringify({
        enabledAt: Date.now(),
      }),
    );

    const debugRef = this.palantirDialogService.openDialog(DebugMenuComponent, {
      size: 'large',
    });

    debugRef.afterClosed().then(() => {
      this.menuIsOpen = false;
    });
  }

  public setDebugMetadata(key: string, value: unknown) {
    const metadata = this.getDebugMetadata();

    metadata[key] = value;

    localStorage.setItem(this.DEBUG_METADATA_KEY, JSON.stringify(metadata));
    this.updateDebugModifyingState();
  }

  public getDebugMetadata(): Record<string, unknown> {
    if (!localStorage.getItem(this.SPECIAL_LOCAL_STORAGE_KEY)) {
      return {};
    }

    const metadata = localStorage.getItem(this.DEBUG_METADATA_KEY);

    if (!metadata) {
      return {};
    }

    try {
      return JSON.parse(metadata);
    } catch {
      debugLogger.error('Error parsing debug metadata.');
      localStorage.removeItem(this.DEBUG_METADATA_KEY);
      return {};
    }
  }

  public getSingleDebugMetadata<T = string>(key: string): T | null {
    return (this.getDebugMetadata()[key] as T) ?? null;
  }

  public clearDebugMetadata(): void {
    localStorage.removeItem(this.DEBUG_METADATA_KEY);
    this.updateDebugModifyingState();
  }

  ngOnDestroy(): void {
    window.removeEventListener('keydown', this.keydownHandler);
  }
}
